import { Component } from '@angular/core';

@Component({
  selector: 'app-audit-glossary-dialog',
  templateUrl: './audit-glossary-dialog.component.html',
})
export class AuditGlossaryDialogComponent {
  search = '';

  readonly sections = [
    {
      title: 'Contexto do evento',
      icon: 'subject',
      terms: [
        { name: 'Ator', code: 'actor', description: 'Conta que executou ou tentou executar a ação. Pode ficar não identificada quando a autenticação falha.' },
        { name: 'Alvo', code: 'target', description: 'Registro ou recurso afetado pela ação, como usuário, aluno, programa, pagamento ou configuração.' },
        { name: 'Ação', code: 'action', description: 'Nome técnico e pesquisável da operação registrada. Ações iniciadas por admin.write representam alterações capturadas automaticamente pelo middleware.' },
        { name: 'Request ID', code: 'request_id', description: 'Identificador de uma requisição. Eventos com o mesmo valor pertencem à mesma sequência operacional.' },
        { name: 'Token fingerprint', code: 'token_fingerprint', description: 'Hash SHA-256 usado para correlacionar acessos do mesmo token sem armazenar o JWT original.' },
        { name: 'Session fingerprint', code: 'session_fingerprint', description: 'Hash derivado de IP, navegador e idioma. Ajuda a agrupar contexto, mas não identifica sozinho uma pessoa ou dispositivo.' },
        { name: 'IP', code: 'ip', description: 'Endereço observado pela API. Proxies, VPNs e redes móveis podem fazer o mesmo usuário aparecer com IPs diferentes.' },
        { name: 'User-agent', code: 'user_agent', description: 'Identificação enviada pelo navegador ou aplicativo, incluindo versão e sistema operacional.' },
      ],
    },
    {
      title: 'Tipos de evento',
      icon: 'category',
      terms: [
        { name: 'Autenticação', code: 'auth', description: 'Login, logout, emissão ou renovação de token e rejeições de autenticação.' },
        { name: 'Consulta', code: 'db_read', description: 'Leitura administrativa sensível registrada para investigação.' },
        { name: 'Alteração', code: 'db_write', description: 'Criação ou modificação de dados feita por uma conta administrativa.' },
        { name: 'Exclusão', code: 'delete', description: 'Solicitação, soft delete, exclusão definitiva ou tentativa relacionada a remoção de dados.' },
        { name: 'Restauração', code: 'restore', description: 'Recuperação ou tentativa de recuperar um registro removido por soft delete.' },
        { name: 'Configuração', code: 'config_change', description: 'Mudança em parâmetros globais ou operacionais do sistema.' },
        { name: 'Mudança de status', code: 'status_change', description: 'Ativação, bloqueio, desbloqueio ou outra alteração de estado de uma conta ou recurso.' },
        { name: 'Permissão negada', code: 'permission_denied', description: 'A API recusou a ação porque a conta autenticada não possuía a permissão exigida.' },
        { name: 'Alerta de segurança', code: 'security_warning', description: 'A requisição apresentou uma inconsistência que merece análise. Não comprova, isoladamente, uma invasão.' },
      ],
    },
    {
      title: 'Status',
      icon: 'rule',
      terms: [
        { name: 'Sucesso', code: 'success', description: 'A operação registrada foi concluída.' },
        { name: 'Bloqueado', code: 'blocked', description: 'A operação foi recusada por permissão, validação ou regra de negócio.' },
        { name: 'Falha', code: 'failed', description: 'A operação foi iniciada, mas terminou com erro técnico ou operacional.' },
      ],
    },
    {
      title: 'Sinais de risco',
      icon: 'warning',
      terms: [
        { name: 'Primeiro login observado', code: 'first_observed_login', description: 'Primeiro login bem-sucedido encontrado desde o início da auditoria. Não significa primeiro login da vida da conta nem indica invasão sozinho.' },
        { name: 'Novo IP', code: 'new_ip', description: 'Login bem-sucedido em um IP ainda não encontrado no histórico auditado da conta. Pode ocorrer por troca de rede, VPN ou provedor.' },
        { name: 'Novo dispositivo', code: 'new_user_agent', description: 'Navegador, aplicativo ou versão ainda não encontrado nos logins auditados da conta.' },
        { name: 'Falhas de login repetidas', code: 'repeated_failed_logins', description: 'Foram observadas pelo menos três falhas de login para a conta em uma janela de 15 minutos.' },
        { name: 'Sucesso após falhas', code: 'success_after_repeated_failures', description: 'Um login teve sucesso após pelo menos três falhas recentes para a mesma conta.' },
        { name: 'Token rejeitado repetidamente', code: 'repeated_rejected_token', description: 'O mesmo fingerprint de token acumulou rejeições em uma janela de 15 minutos.' },
        { name: 'Token em múltiplos IPs', code: 'same_token_multiple_ips', description: 'O mesmo fingerprint de token já apareceu associado a outro IP. Deve ser confrontado com VPN, proxy e rede móvel antes de concluir uso indevido.' },
        { name: 'Token ausente', code: 'token_missing', description: 'A rota exigia autenticação, mas a requisição não enviou um token.' },
        { name: 'Token inválido', code: 'token_invalid', description: 'O token enviado não pôde ser validado, por formato, assinatura ou conteúdo incompatível.' },
        { name: 'Token expirado', code: 'token_expired', description: 'O prazo de validade do token terminou e a API recusou a autenticação.' },
        { name: 'Usuário do token não encontrado', code: 'token_user_not_found', description: 'O token era legível, mas a conta indicada por ele não foi encontrada ou não está mais disponível.' },
        { name: 'Ação destrutiva', code: 'destructive_action', description: 'A requisição pode excluir ou limpar dados.' },
        { name: 'Operação em massa', code: 'bulk_action', description: 'A ação pode afetar vários registros em uma única requisição.' },
        { name: 'Requisição com erro', code: 'request_failed', description: 'A API respondeu com status HTTP 4xx ou 5xx.' },
        { name: 'Exceção técnica', code: 'request_exception', description: 'Uma exceção interrompeu a execução da requisição.' },
        { name: 'Master obrigatório', code: 'master_required', description: 'Uma conta que não era master tentou acessar uma função exclusiva do master.' },
        { name: 'Possível tentativa contra outra conta', code: 'possible_account_targeting', description: 'A requisição informou dados pertencentes a uma conta diferente da conta autenticada.' },
        { name: 'ID de exclusão divergente', code: 'delete_target_user_mismatch', description: 'O ID informado na solicitação de exclusão não corresponde ao usuário autenticado.' },
        { name: 'E-mail de exclusão divergente', code: 'delete_target_email_mismatch', description: 'O e-mail informado na solicitação de exclusão não corresponde ao usuário autenticado.' },
        { name: 'Suporte obrigatório', code: 'support_required', description: 'A tentativa foi bloqueada porque a exclusão da conta deve ser conduzida pelo suporte.' },
      ],
    },
  ];

  filteredTerms(section: any) {
    const query = this.normalize(this.search);
    if (!query) return section.terms;

    return section.terms.filter((term: any) => this.normalize(
      `${term.name} ${term.code} ${term.description}`
    ).indexOf(query) !== -1);
  }

  hasResults() {
    return this.sections.some((section: any) => this.filteredTerms(section).length > 0);
  }

  private normalize(value: string) {
    return (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }
}
